import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { useData } from "../hooks/useData";
import { IMAGE_PRESETS, type BackgroundType, type BackgroundSetting } from "../hooks/useOrgBackground";

// =============================================================================
// BACKGROUND CONTEXT
// =============================================================================
// Provides shared background state across the app so changes are instant.
// - App.tsx reads backgroundClass/backgroundStyle for rendering
// - BackgroundSettingsPage updates via selectPreset/uploadCustom
// - Both see the same state — no refresh needed
// =============================================================================

interface BackgroundContextType {
  setting: BackgroundSetting | null;
  isLoading: boolean;
  error: string | null;
  backgroundStyle: React.CSSProperties;
  backgroundClass: string;
  selectPreset: (presetName: string, type: BackgroundType) => Promise<void>;
  uploadCustom: (file: File) => Promise<void>;
  refresh: () => Promise<void>;
  isUploading: boolean;
  uploadProgress: number;
}

const BackgroundContext = createContext<BackgroundContextType | undefined>(undefined);

export const BackgroundProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentOrganization } = useData();
  const organizationId = currentOrganization?.id;

  const [setting, setSetting] = useState<BackgroundSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch current setting
  const fetchSetting = useCallback(async () => {
    if (!organizationId || !supabase) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from("org_background_settings")
        .select("*")
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setSetting(data);
    } catch (err) {
      console.error("Failed to fetch background setting:", err);
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  // Select a preset (gradient or image)
  const selectPreset = useCallback(async (presetName: string, type: BackgroundType) => {
    if (!organizationId || !supabase) return;

    try {
      setError(null);
      const payload = {
        organization_id: organizationId,
        background_type: type,
        preset_name: presetName,
        custom_url: null,
      };

      const { data, error: upsertError } = await supabase
        .from("org_background_settings")
        .upsert(payload, { onConflict: "organization_id" })
        .select()
        .single();

      if (upsertError) throw upsertError;
      setSetting(data); // Instant update
    } catch (err) {
      console.error("Failed to save preset:", err);
      setError(err instanceof Error ? err.message : "Failed to save");
      throw err;
    }
  }, [organizationId]);

  // Upload custom image
  const uploadCustom = useCallback(async (file: File) => {
    if (!organizationId || !supabase) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setError("Invalid file type. Use JPEG, PNG, or WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File too large. Maximum 5MB.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      const ext = file.name.split(".").pop() || "png";
      const filename = `${organizationId}/background-${Date.now()}.${ext}`;

      setUploadProgress(30);
      const { error: uploadError } = await supabase.storage
        .from("org-backgrounds")
        .upload(filename, file, { cacheControl: "3600", upsert: true });

      if (uploadError) throw uploadError;
      setUploadProgress(70);

      const { data: urlData } = supabase.storage
        .from("org-backgrounds")
        .getPublicUrl(filename);

      const { data, error: upsertError } = await supabase
        .from("org_background_settings")
        .upsert({
          organization_id: organizationId,
          background_type: "custom" as BackgroundType,
          preset_name: null,
          custom_url: urlData.publicUrl,
        }, { onConflict: "organization_id" })
        .select()
        .single();

      if (upsertError) throw upsertError;
      setUploadProgress(100);
      setSetting(data); // Instant update
    } catch (err) {
      console.error("Failed to upload:", err);
      setError(err instanceof Error ? err.message : "Upload failed");
      throw err;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [organizationId]);

  // Compute background style — useMemo so reference is stable between renders
  const backgroundStyle = useMemo((): React.CSSProperties => {
    if (!setting) return {};

    if (setting.background_type === "custom" && setting.custom_url) {
      return {
        backgroundImage: `url(${setting.custom_url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      };
    }

    if (setting.background_type === "preset" && setting.preset_name) {
      const preset = IMAGE_PRESETS.find(p => p.name === setting.preset_name);
      if (preset) {
        return {
          backgroundImage: `url(${preset.file})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        };
      }
    }

    return {};
  }, [setting]);

  // Get CSS class for gradient presets — useMemo so reference is stable between renders
  const backgroundClass = useMemo((): string => {
    if (!setting) return "bg-offwhite";

    if (setting.background_type === "gradient" && setting.preset_name) {
      return `bg-${setting.preset_name}`;
    }

    if (setting.background_type === "custom" || setting.background_type === "preset") {
      return "";
    }

    return "bg-offwhite";
  }, [setting]);

  // Memoize context value — prevents cascade re-renders on all consumers above HashRouter
  const contextValue = useMemo(() => ({
    setting,
    isLoading,
    error,
    backgroundStyle,
    backgroundClass,
    selectPreset,
    uploadCustom,
    refresh: fetchSetting,
    isUploading,
    uploadProgress,
  }), [setting, isLoading, error, backgroundStyle, backgroundClass, selectPreset, uploadCustom, fetchSetting, isUploading, uploadProgress]);

  return (
    <BackgroundContext.Provider value={contextValue}>
      {children}
    </BackgroundContext.Provider>
  );
};

export const useBackground = () => {
  const context = useContext(BackgroundContext);
  if (!context) {
    throw new Error("useBackground must be used within a BackgroundProvider");
  }
  return context;
};
